/**
 * Tag of the element `ps-modal` moves its own host into when it opens
 * (`modal.wc.ts`, `update-portal-content`). Written out here because the
 * design-system barrel cannot be imported by this package's unit tests.
 */
const PORTAL_DESTINATION_TAG = 'ps-portal-destination';

/**
 * PSTUDIO-4555 — take a `ps-modal` out of the document when the component that
 * owns it is going away while the modal is open.
 *
 * Once a modal has opened, its host sits in a portal destination in
 * `document.body`, outside the tree React unmounts. Observed live on a dev
 * server: after leaving the page the host was still there, `opened`, covering
 * the whole page, and its Cancel button no longer reacted. Removing the host
 * runs the design system's own `disconnectedCallback` (focus trap, click-away
 * listener, body scroll locks).
 *
 * It acts only on a host that IS inside a portal destination. A modal that was
 * never opened, or has finished closing (the design system puts it back under
 * its original parent then), is left to React: removing it would break the
 * component when React 18 StrictMode runs an effect's cleanup on mount.
 *
 * Limitation: if the host is a top-level node of the subtree React is removing
 * (the modal rendered at fragment level), React calls `removeChild` on it next
 * and that throws once the host has been moved, whether or not this ran.
 */
export const removeDisplacedModal = (modal: Element | null | undefined) => {
  if (!modal?.closest(PORTAL_DESTINATION_TAG)) return;

  modal.remove();
};
